import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { Component, OnInit, inject } from '@angular/core';
import {
  ClearFormDataActionWithId,
  SetFormActionWithId,
  UpdateFormDataActionWithId,
} from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { CompaniesHouseService } from '@app/finxone-web-frontend/app/lib/services/companies-house-service/companies-house.service';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { NavigationService } from '@app/finxone-web-frontend/app/lib/services/navigation-service/navigation.service';
import { RoleState } from '@app/finxone-web-frontend/app/lib/state/role.state';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { FormActionTypeEnum } from '@finxone-platform/form-action';
import Handlebars from 'handlebars';
import {
  BaseWidgetProperties,
  CompaniesHouseAdditionalCriteria,
  CompaniesHouseNumberResult,
  CompaniesHouseSearchMode,
  CompaniesHouseSearchResponse,
  CompaniesHouseSearchResult,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  baseCssConfigStyle,
  buttonNestedWidgetConfigStyles,
  commonContentStyleList,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { PARENT_FORM_ROW_GAP } from '@app/finxone-web-frontend/app/lib/utils/di-tokens';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-companies-house-search-widget',
  templateUrl: './companies-house-search-widget.component.html',
  styleUrls: ['./companies-house-search-widget.component.scss'],
})
export class CompaniesHouseSearchWidgetComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  public searchForm: FormGroup;
  public searchMode: CompaniesHouseSearchMode;
  public additionalCriteria: CompaniesHouseAdditionalCriteria[];
  public separatorLabel: string;
  public businessNumberLabel: string;
  public companyNameLabel: string;
  public locationLabel: string;
  public isSearching = false;
  private validationMessages: Record<string, string> = {};
  public searchButtonLabel = 'Search';
  public searchButtonStyle: baseCssConfigStyle = {};
  public globalStylingClass = '';
  public isAllowedGlobalStyling = false;

  public readonly SearchMode = CompaniesHouseSearchMode;
  public readonly AdditionalCriteria = CompaniesHouseAdditionalCriteria;

  private widgetProperties: BaseWidgetProperties;
  private parentFormId: string | null = null;
  private searchButtonNestedWidget: UiZoneWidgetAttributeConfig | undefined;

  private readonly store = inject(Store);
  private readonly configService = inject(ConfigService);
  private readonly companiesHouseService = inject(CompaniesHouseService);
  private readonly navigationService = inject(NavigationService);
  private readonly countriesCurrenciesService = inject(CountriesCurrenciesService);
  private readonly iconCacheService = inject(IconCacheService);
  public dataIn = inject<ProviderClass<UiZoneWidgetAttributeConfig>>(ProviderClass);
  public parentFormWidgetRowGap: string | undefined | null = inject(PARENT_FORM_ROW_GAP, { optional: true });

  public attributes: UiZoneWidgetAttributeConfig;
  public contentStyle: baseCssConfigStyle;

  ngOnInit(): void {
    this.initializeAttriAndStyles();
    this.initializeWidgetProperties();
    this.initializeSearchButton();
    this.buildForm();
  }

  private initializeAttriAndStyles(): void {
    if (this.dataIn?.attri) {
      this.attributes = this.dataIn.attri;
      this.widgetProperties = this.dataIn.attri.widgetProperties;
    }
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attributes, commonContentStyleList);
  }

  private initializeWidgetProperties(): void {
    this.searchMode = this.widgetProperties?.['searchMode'] ?? CompaniesHouseSearchMode.BUSINESS_NUMBER_ONLY;
    this.additionalCriteria = this.widgetProperties?.['additionalSearchCriteria'] ?? [];
    this.businessNumberLabel = this.widgetProperties?.['businessNumberLabel'] ?? 'Business number';
    this.companyNameLabel = this.widgetProperties?.['companyNameLabel'] ?? 'Company name';
    this.locationLabel = this.widgetProperties?.['locationLabel'] ?? 'Location';
    this.parentFormId = this.widgetProperties?.['parentForm'] ?? null;
    this.validationMessages = this.widgetProperties?.['validationMessages'] ?? {};

    const rawSeparatorLabel = this.widgetProperties?.['separatorLabel'] ?? '';
    const separatorTemplate = Handlebars.compile(rawSeparatorLabel);
    buildHandleBarModel(this.store, rawSeparatorLabel, this.countriesCurrenciesService, this.iconCacheService)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.separatorLabel = filterIfHandlebarNotRendered(separatorTemplate(model));
      });
  }

  private initializeSearchButton(): void {
    const searchButtonConfig = this.dataIn.widgetConfig?.nestedWidgets?.['searchButton'];
    this.searchButtonNestedWidget = searchButtonConfig;
    this.searchButtonLabel = searchButtonConfig?.widgetProperties?.['textContent'] || 'Search';
    this.searchButtonStyle = searchButtonConfig
      ? this.configService.getSpecificBaseCssStyle(searchButtonConfig, buttonNestedWidgetConfigStyles)
      : {};
    this.getGlobalStyling(searchButtonConfig);
  }

  private getGlobalStyling(searchButtonConfig: UiZoneWidgetAttributeConfig | undefined): void {
    if (
      searchButtonConfig?.widgetProperties?.['globalStyling'] &&
      searchButtonConfig?.widgetProperties?.['globalStylingClass']
    ) {
      this.isAllowedGlobalStyling = true;
      this.globalStylingClass = searchButtonConfig.widgetProperties['globalStylingClass'];
    }
  }

  private buildForm(): void {
    this.searchForm = new FormGroup({
      businessNumber: new FormControl('', { nonNullable: true }),
      companyName: new FormControl('', { nonNullable: true }),
      location: new FormControl('', { nonNullable: true }),
    });
  }

  public get showCompanyName(): boolean {
    return (
      this.searchMode === CompaniesHouseSearchMode.BUSINESS_NUMBER_AND_ADDITIONAL &&
      this.additionalCriteria.includes(CompaniesHouseAdditionalCriteria.COMPANY_NAME)
    );
  }

  public get showLocation(): boolean {
    return (
      this.searchMode === CompaniesHouseSearchMode.BUSINESS_NUMBER_AND_ADDITIONAL &&
      this.additionalCriteria.includes(CompaniesHouseAdditionalCriteria.LOCATION)
    );
  }

  public get showSeparator(): boolean {
    return this.showCompanyName || this.showLocation;
  }

  public onSearch(): void {
    if (this.isSearching) return;

    const businessNumberControl = this.searchForm.get('businessNumber');
    const companyNameControl = this.searchForm.get('companyName');
    const locationControl = this.searchForm.get('location');

    const businessNumber = businessNumberControl?.value?.trim();
    const companyName = companyNameControl?.value?.trim();
    const location = locationControl?.value?.trim();

    this.clearValidationErrors();

    if (!businessNumber && !this.isValidAdditionalCriteria(companyName, location)) {
      this.reportAdditionalCriteriaErrors(
        businessNumberControl,
        companyNameControl,
        locationControl,
        companyName,
        location,
      );
      return;
    }

    this.isSearching = true;

    const searchObs: Observable<CompaniesHouseSearchResponse | CompaniesHouseNumberResult> = businessNumber
      ? this.companiesHouseService.checkCompanyNumber(businessNumber)
      : this.companiesHouseService.searchCompanies({
          companyName: companyName || undefined,
          location: location || undefined,
        });

    searchObs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: CompaniesHouseSearchResponse | CompaniesHouseNumberResult) => {
        this.isSearching = false;
        const items: CompaniesHouseSearchResult[] =
          'result' in response ? response.result : response.companyNumber ? [response] : [];

        this.handleSearchResults(items);
      },
      error: () => {
        this.isSearching = false;
        this.store.dispatch(new ClearFormDataActionWithId(FormActionTypeEnum.COMPANIES_HOUSE_SEARCH));
      },
    });
  }

  private isValidAdditionalCriteria(companyName: string | undefined, location: string | undefined): boolean {
    if (this.showCompanyName && this.showLocation) {
      return !!companyName && !!location;
    }
    if (this.showCompanyName) {
      return !!companyName;
    }
    if (this.showLocation) {
      return !!location;
    }
    return false;
  }

  private reportAdditionalCriteriaErrors(
    businessNumberControl: AbstractControl | null,
    companyNameControl: AbstractControl | null,
    locationControl: AbstractControl | null,
    companyName: string | undefined,
    location: string | undefined,
  ): void {
    if (this.showCompanyName && this.showLocation) {
      if (!companyName && !location) {
        this.setControlError(
          businessNumberControl,
          this.validationMessages['businessNumberOrBoth'] ??
            'Please enter a business number, or both company name and location.',
        );
      } else if (!companyName) {
        this.setControlError(
          companyNameControl,
          this.validationMessages['companyNameRequired'] ??
            'Company name is required when a location is entered.',
        );
      } else {
        this.setControlError(
          locationControl,
          this.validationMessages['locationRequired'] ??
            'Location is required when a company name is entered.',
        );
      }
      return;
    }

    if (this.showCompanyName) {
      this.setControlError(
        businessNumberControl,
        this.validationMessages['businessNumberOrCompanyName'] ??
          'Please enter a business number or company name.',
      );
      return;
    }

    if (this.showLocation) {
      this.setControlError(
        businessNumberControl,
        this.validationMessages['businessNumberOrLocation'] ?? 'Please enter a business number or location.',
      );
      return;
    }

    this.setControlError(
      businessNumberControl,
      this.validationMessages['businessNumberRequired'] ?? 'Please enter a business number.',
    );
  }

  private clearValidationErrors(): void {
    ['businessNumber', 'companyName', 'location'].forEach((controlName) => {
      const control = this.searchForm.get(controlName);
      if (control?.errors) {
        control.setErrors(null);
      }
    });
  }

  private setControlError(control: AbstractControl | null | undefined, message: string): void {
    if (!control) return;
    control.setErrors({ required: message });
    control.markAsDirty();
    control.markAsTouched();
  }

  private handleSearchResults(items: CompaniesHouseSearchResult[]): void {
    if (items.length === 0) {
      this.dispatchSearchData({ companyHouseSearchResult: [] });
      this.navigateForOutcome();
      return;
    }

    const baseData = {
      companyHouseSearchResult: items,
    };
    this.dispatchSearchData(baseData);
    this.navigateForOutcome();
  }

  private dispatchSearchData(data: Record<string, unknown>): void {
    if (this.parentFormId) {
      this.store.dispatch(new UpdateFormDataActionWithId(data, this.parentFormId));
    } else {
      this.store.dispatch(
        new SetFormActionWithId(
          { type: FormActionTypeEnum.COMPANIES_HOUSE_SEARCH, formData: data },
          FormActionTypeEnum.COMPANIES_HOUSE_SEARCH,
        ),
      );
    }
  }

  private navigateForOutcome(): void {
    if (!this.searchButtonNestedWidget) return;

    const { pageUrl } = this.navigationService.buildPageNavigationUrl({
      pageId: this.searchButtonNestedWidget['pageId'] || '',
      zone: this.searchButtonNestedWidget['zone'],
      pageUrl: this.searchButtonNestedWidget['pageUrl'],
      role: this.searchButtonNestedWidget['role'],
      navigationOption: this.searchButtonNestedWidget['navigationOption'],
      tabRef: this.searchButtonNestedWidget['tabRef'],
      pageTransition: this.searchButtonNestedWidget['pageTransition'] || 'forward',
    });

    this.navigationService.navigateByPageUrl({
      url: pageUrl,
      targetRole: this.searchButtonNestedWidget['role'],
      currentRole: this.store.selectSnapshot(RoleState.getRole)?.role,
      zone: this.searchButtonNestedWidget['zone'],
      externalLink: this.searchButtonNestedWidget['externalUrl'],
      browserBehaviour: this.searchButtonNestedWidget['browserBehaviour'],
    });
  }
}
